import { KeyboardEvent, useCallback } from 'react';
import type { BranchLocation, LocationId, Location } from '../../types';
import { isBranch } from '../../utils/validations';
import type {
  UseKeyboardNavigationOptions,
  UseKeyboardNavigationResult,
} from './types';
import normalizeLocationId from '../../utils/normalizeLocationId';

export default function useKeyboardNavigation({
  tree,
  nodeIndex,
  focusedNodeId,
  setFocusedNodeId,
  onToggleOpen,
  disabled = false,
  onSelectNode,
}: UseKeyboardNavigationOptions): UseKeyboardNavigationResult {
  const getCurrentNode = useCallback((): Location | null => {
    if (!focusedNodeId) return null;

    return nodeIndex[focusedNodeId]?.node ?? null;
  }, [focusedNodeId, nodeIndex]);

  const getNextSibling = useCallback(
    (nodeId: LocationId): Location | null => {
      const entry = nodeIndex[normalizeLocationId(nodeId)];

      if (!entry) return null;

      const { parent, siblingIndex } = entry;
      const nextIndex = siblingIndex + 1;

      if (nextIndex >= parent.children.length) return null;

      return parent.children[nextIndex];
    },
    [nodeIndex]
  );

  const getPrevSibling = useCallback(
    (nodeId: LocationId): Location | null => {
      const entry = nodeIndex[normalizeLocationId(nodeId)];

      if (!entry) return null;

      const { parent, siblingIndex } = entry;
      const prevIndex = siblingIndex - 1;

      if (prevIndex < 0) return null;

      return parent.children[prevIndex];
    },
    [nodeIndex]
  );

  const getLastVisibleDescendant = useCallback((node: Location): Location => {
    if (!isBranch(node) || !node.isOpen || node.children.length === 0) {
      return node;
    }

    const lastChild = node.children[node.children.length - 1];

    return getLastVisibleDescendant(lastChild);
  }, []);

  const getVisibleParent = useCallback(
    (nodeId: LocationId): BranchLocation | null => {
      const entry = nodeIndex[normalizeLocationId(nodeId)];

      if (!entry) return null;

      const { parent } = entry;

      if (!nodeIndex[normalizeLocationId(parent.id)]) return null;

      return parent;
    },
    [nodeIndex]
  );

  const navigateDown = useCallback(() => {
    const currentNode = getCurrentNode();
    if (!currentNode) return;

    // If open branch with children → first child
    if (
      isBranch(currentNode) &&
      currentNode.isOpen &&
      currentNode.children.length > 0
    ) {
      setFocusedNodeId(normalizeLocationId(currentNode.children[0].id));
      return;
    }

    // If has next sibling → next sibling
    const nextSibling = getNextSibling(normalizeLocationId(currentNode.id));

    if (nextSibling) {
      setFocusedNodeId(normalizeLocationId(nextSibling.id));
      return;
    }

    // Go up ancestors to find one with next sibling
    let ancestor = getVisibleParent(normalizeLocationId(currentNode.id));

    while (ancestor) {
      const ancestorNextSibling = getNextSibling(
        normalizeLocationId(ancestor.id)
      );

      if (ancestorNextSibling) {
        setFocusedNodeId(normalizeLocationId(ancestorNextSibling.id));
        return;
      }

      ancestor = getVisibleParent(normalizeLocationId(ancestor.id));
    }
  }, [getCurrentNode, getNextSibling, getVisibleParent, setFocusedNodeId]);

  const navigateUp = useCallback(() => {
    const currentNode = getCurrentNode();
    if (!currentNode) return;

    // If has previous sibling → last visible descendant of that sibling
    const prevSibling = getPrevSibling(normalizeLocationId(currentNode.id));
    if (prevSibling) {
      setFocusedNodeId(
        normalizeLocationId(getLastVisibleDescendant(prevSibling).id)
      );
      return;
    }

    // Otherwise → go to parent
    const parent = getVisibleParent(normalizeLocationId(currentNode.id));

    if (!parent) return;

    setFocusedNodeId(normalizeLocationId(parent.id));
  }, [
    getCurrentNode,
    getPrevSibling,
    getLastVisibleDescendant,
    getVisibleParent,
    setFocusedNodeId,
  ]);

  const expandOrNavigateToChild = useCallback(() => {
    const currentNode = getCurrentNode();
    if (!currentNode || !isBranch(currentNode)) return;
    if (currentNode.isLoading) return;

    if (!currentNode.isOpen) {
      onToggleOpen(currentNode);
      return;
    }

    if (currentNode.children.length > 0) {
      setFocusedNodeId(normalizeLocationId(currentNode.children[0].id));
    }
  }, [getCurrentNode, onToggleOpen, setFocusedNodeId]);

  const collapseOrNavigateToParent = useCallback(() => {
    const currentNode = getCurrentNode();
    if (!currentNode) return;

    if (isBranch(currentNode) && currentNode.isLoading) return;

    if (isBranch(currentNode) && currentNode.isOpen) {
      onToggleOpen(currentNode);

      return;
    }

    const parent = getVisibleParent(normalizeLocationId(currentNode.id));

    if (!parent) return;

    setFocusedNodeId(normalizeLocationId(parent.id));
  }, [getCurrentNode, getVisibleParent, onToggleOpen, setFocusedNodeId]);

  const navigateToStart = useCallback(() => {
    const [root] = tree;
    if (root.children.length === 0) return;

    setFocusedNodeId(normalizeLocationId(root.children[0].id));
  }, [tree, setFocusedNodeId]);

  const navigateToEnd = useCallback(() => {
    const [root] = tree;

    if (root.children.length === 0) return;

    const lastTopLevel = root.children[root.children.length - 1];

    setFocusedNodeId(
      normalizeLocationId(getLastVisibleDescendant(lastTopLevel).id)
    );
  }, [tree, getLastVisibleDescendant, setFocusedNodeId]);

  const toggleCurrentNode = useCallback(() => {
    const currentNode = getCurrentNode();

    if (!currentNode || !isBranch(currentNode)) return;

    if (currentNode.isLoading) return;

    onToggleOpen(currentNode);
  }, [getCurrentNode, onToggleOpen]);

  const selectCurrentNode = useCallback(() => {
    const currentNode = getCurrentNode();
    if (!currentNode) return;

    onSelectNode(currentNode);
    setFocusedNodeId(normalizeLocationId(currentNode.id));
  }, [getCurrentNode, setFocusedNodeId, onSelectNode]);

  const toggleOrSelectCurrentNode = useCallback(() => {
    const currentNode = getCurrentNode();
    if (!currentNode) return;

    if (isBranch(currentNode)) {
      toggleCurrentNode();
    } else {
      selectCurrentNode();
    }
  }, [getCurrentNode, toggleCurrentNode, selectCurrentNode]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (disabled) return;

      const preventDefault = () => {
        event.preventDefault();
        event.stopPropagation();
      };

      const keyActions: Record<string, () => void> = {
        ArrowDown: () => {
          preventDefault();
          navigateDown();
        },
        ArrowUp: () => {
          preventDefault();
          navigateUp();
        },
        ArrowRight: () => {
          preventDefault();
          expandOrNavigateToChild();
        },
        ArrowLeft: () => {
          preventDefault();
          collapseOrNavigateToParent();
        },
        Home: () => {
          preventDefault();
          navigateToStart();
        },
        End: () => {
          preventDefault();
          navigateToEnd();
        },
        Enter: () => {
          preventDefault();
          toggleOrSelectCurrentNode();
        },
        ' ': () => {
          preventDefault();
          selectCurrentNode();
        },
        Spacebar: () => {
          preventDefault();
          selectCurrentNode();
        },
      };

      keyActions[event.key]?.();
    },
    [
      disabled,
      focusedNodeId,
      navigateDown,
      navigateUp,
      expandOrNavigateToChild,
      collapseOrNavigateToParent,
      navigateToStart,
      navigateToEnd,
      toggleCurrentNode,
      selectCurrentNode,
      toggleOrSelectCurrentNode,
    ]
  );

  return { handleKeyDown };
}
